import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { MarketingSEO } from '@/components/seo/MarketingSEO';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type { SmartAlertPage } from '@/data/smartAlertPages';
import { Link } from 'react-router-dom';

interface SmartAlertTemplateProps {
  page: SmartAlertPage;
}

export const SmartAlertTemplate: React.FC<SmartAlertTemplateProps> = ({ page }) => {
  const canonicalUrl = `https://www.joeyfamily.com${page.path}`;
  const seoTitle = page.seoTitle ?? `${page.title} Smart Alert | Joey`;
  const seoDescription = page.seoDescription ?? page.summary;

  return (
    <>
      <MarketingSEO
        title={seoTitle}
        description={seoDescription}
        url={canonicalUrl}
        keywords={page.seoKeywords}
        image="/img/og/joey-default-1200x630.png"
      />

      <Navigation />

      <main className="min-h-screen bg-gradient-to-b from-white via-slate-50 to-white">
        {/* Hero */}
        <section className="pt-32 pb-16 px-4">
          <div className="max-w-5xl mx-auto grid md:grid-cols-[1fr,320px] gap-10 items-center">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${page.accentColor}`}>
                  <img src={page.icon.src} alt={page.icon.alt} className="w-8 h-8" loading="lazy" />
                </div>
                <span className={`text-sm font-semibold uppercase tracking-wide ${page.accentText}`}>
                  Smart Alert category
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl font-semibold text-brand-navy mb-6">{page.title}</h1>
              <p className="text-xl text-slate-700 mb-6">{page.heroSubtitle}</p>
              <p className="text-slate-600 leading-relaxed mb-8">{page.summary}</p>
              <Button asChild size="lg">
                <Link to="/get-started">Get Joey Smart Alerts</Link>
              </Button>
            </div>

            <Card className="bg-white border-0 shadow-lg">
              <CardContent className="p-6 space-y-4">
                <h2 className="text-lg font-semibold text-brand-navy">What this alert monitors</h2>
                <ul className="space-y-3 text-slate-600">
                  {page.overview.map((item) => (
                    <li key={item} className="flex gap-3">
                      <span className="mt-1 h-2 w-2 rounded-full bg-brand-cyan" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Coaching tips */}
        <section className="py-16 px-4 bg-white border-t border-slate-100">
          <div className="max-w-4xl mx-auto space-y-6">
            <h2 className="text-3xl font-semibold text-brand-navy">Coaching next steps</h2>
            <p className="text-slate-600 leading-relaxed">
              Every family conversation is different. Treat these ideas as starting points and add your own context,
              values, and tone. Replace the placeholder copy here with your own notes when you build the full guide.
            </p>
            <div className="space-y-4">
              {page.coachingTips.map((tip) => (
                <div key={tip} className="p-4 rounded-2xl bg-slate-50 border border-slate-100 text-slate-700">
                  {tip}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Placeholder content reminder */}
        <section className="py-16 px-4 bg-slate-900 text-white">
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-10">
            <div>
              <h3 className="text-2xl font-semibold mb-4">Add screenshots and stories</h3>
              <p className="text-lg text-white/90">
                Drop in Smart Alert screenshots, anonymized transcripts, or founder commentary. Keep each section
                focused on the real coaching moment parents will face once this alert fires.
              </p>
            </div>
            <div className="space-y-4 text-white/90">
              <p>Placeholder ideas:</p>
              <ul className="space-y-2 list-disc list-inside">
                <li>Real-world example (scrub all identifying info).</li>
                <li>Guided script: “When I see this alert I usually say…”</li>
                <li>Resources to share with teens (helplines, articles, or family agreements).</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="py-12 px-4 bg-white border-t border-slate-100">
          <div className="max-w-5xl mx-auto flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-widest text-slate-500">Keep exploring</p>
              <h3 className="text-2xl font-semibold text-brand-navy mt-2">See all Smart Alert categories</h3>
              <p className="text-slate-600 mt-2">
                Each alert has its own landing page with context, scripts, and screenshots. Head back to the Smart Alerts
                overview to browse every category at once.
              </p>
            </div>
            <Button asChild size="lg" className="shrink-0">
              <Link to="/features/smart-alerts">Return to Smart Alerts</Link>
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default SmartAlertTemplate;

